import React, { useRef, useEffect, useState } from 'react';
import { useTheme } from '@material-ui/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import randomString from 'random-string';
import { useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import _ from 'lodash';

import {
    IconButton,
    Text,
    StyledCard,
    StyledCardContent,
    CrossIcon,
    Avatar,
    GoBackButton,
} from '../index';

import { TextGroup, ChatInput } from '../Message/ChatWindow';

import socket from '../../socket';

import { encrypt, decrypt } from '../../utils/messages';

import { GET_OPEN_MESSAGES } from '../../services';

import { alertError } from '../../redux/stateActions';

const containerStyle = (mdUp) => {
    return {
        zIndex: 20,
        height: '100vh',
        width: mdUp ? '415px' : '100%',
        backgroundColor: 'white',
        padding: '0',
        position: 'fixed',
        inset: mdUp ? '0 0 0 auto' : 0,
        borderRadius: mdUp ? '16px' : 0,
    };
};

export const CallMemberCard = ({ open, participants, onClose }) => {
    const theme = useTheme();
    const mdUp = useMediaQuery(theme.breakpoints.up('sm'));

    const headerRef = useRef(null);
    const [contentHeight, setContentHeight] = useState('100vh');
    const handleResize = () => {
        if (headerRef.current) {
            const headerHeight = headerRef.current.clientHeight;

            setContentHeight(`${window.innerHeight - headerHeight}px`);
        }
    };
    useEffect(() => {
        if (open) {
            handleResize();
        }
    }, [open]);

    window.addEventListener('resize', handleResize);

    return (
        open && (
            <StyledCard style={containerStyle(mdUp)}>
                <StyledCardContent
                    style={{
                        display: 'grid',
                        gridTemplateRows: 'auto 1fr',
                    }}
                >
                    <div
                        ref={headerRef}
                        style={{
                            padding: '10px 20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            boxShadow: '1px 2px 12px 1px rgba(71, 77, 108, 0.04)',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {!mdUp && <GoBackButton onClick={onClose} />}
                            <Text $fontSize="20px" $bold>
                                MEMBERS
                            </Text>
                        </div>

                        <div>
                            {mdUp && (
                                <IconButton onClick={onClose}>
                                    <CrossIcon fill="black" />
                                </IconButton>
                            )}
                        </div>
                    </div>
                    <div
                        style={{
                            height: contentHeight,
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            paddingBottom: '20px',
                        }}
                    >
                        {participants.map((p) => (
                            <div
                                key={p.id}
                                style={{
                                    display: 'flex',
                                    columnGap: '20px',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    padding: '10px 20px',
                                }}
                            >
                                <Avatar
                                    $avatarSize="54px"
                                    $borderRadius="50%"
                                    src={p.user?.avatar?.url}
                                    alt={p.user?.fullname}
                                />
                                <Text $bold>{p.user?.fullname}</Text>
                            </div>
                        ))}
                    </div>
                </StyledCardContent>
            </StyledCard>
        )
    );
};

CallMemberCard.propTypes = {
    open: PropTypes.bool.isRequired,
    participants: PropTypes.arrayOf(PropTypes.object),
    onClose: PropTypes.func.isRequired,
};

export const MessagesCard = ({
    open,
    onClose,
    sender,
    user,
    sessionId,
    messagesCount,
    setMessagesCount,
}) => {
    const dispatch = useDispatch();

    const theme = useTheme();
    const mdUp = useMediaQuery(theme.breakpoints.up('sm'));

    const headerRef = useRef(null);
    const inputContainerRef = useRef(null);

    const [contentHeight, setContentHeight] = useState('100vh');
    const handleResize = () => {
        if (headerRef.current) {
            const headerHeight = headerRef.current.clientHeight;
            const inputContainerHeight = inputContainerRef.current.clientHeight;

            setContentHeight(`${window.innerHeight - headerHeight - inputContainerHeight}px`);
        }
    };
    useEffect(() => {
        if (open) {
            setMessagesCount(0);
            handleResize();
        }
    }, [open]);

    window.addEventListener('resize', handleResize);

    /*---------- Messages -----------*/
    const [role, setRole] = useState(null);

    useEffect(() => {
        setRole(localStorage.getItem('role'));
    }, []);

    const pickerRef = useRef(null);
    const [openPicker, setOpenPicker] = useState(false);
    const [messagesData, setMessagesData] = useState([]);
    const [inputValue, setInputValue] = useState('');

    // hide picker if click somewhere else
    useEffect(() => {
        document.addEventListener('click', handleClickOutside, false);
        return () => {
            document.removeEventListener('click', handleClickOutside, false);
        };
    }, []);

    const handleClickOutside = (event) => {
        if (pickerRef.current && !pickerRef.current.contains(event.target)) {
            setOpenPicker(false);
        }
    };

    const onChange = (e) => {
        setInputValue(e.target.value);
    };

    const handleAddLineBreak = () => {
        setInputValue(`${inputValue}\n`);
    };

    const onEmojiClick = (event, emojiObject) => {
        setInputValue(inputValue + emojiObject.emoji);
    };

    const handleOpenPicker = () => {
        setOpenPicker(!openPicker);
    };

    const handleSendMessage = async () => {
        if (inputValue === '') return;

        setOpenPicker(false);

        // Encrypt
        const encryptData = encrypt(inputValue, sender + sessionId);
        const newMessagesData = JSON.parse(JSON.stringify(messagesData));
        const tempId = randomString({ length: 24 }).toLowerCase();

        const newMessage = {
            tempId: tempId,
            sender: sender,
            sessionId: sessionId,
            text: inputValue,
            createAt: dayjs,
            staff: user.staff
                ? { user: { fullname: user.fullname, avatar: user.avatar } }
                : undefined,
            patient: user.patient
                ? { user: { fullname: user.fullname, avatar: user.avatar } }
                : undefined,
        };

        newMessagesData.unshift({ ...newMessage, error: false, encryptText: encryptData });
        setMessagesData(newMessagesData);

        const data = {
            tempId: tempId,
            sender: sender,
            senderRole: role,
            sessionId: sessionId,
            text: encryptData,
            staff: user.staff
                ? { user: { fullname: user.fullname, avatar: user.avatar } }
                : undefined,
            patient: user.patient
                ? { user: { fullname: user.fullname, avatar: user.avatar } }
                : undefined,
        };

        socket.emit('sendOpenMessage', data);

        setInputValue('');
    };

    const handleRetrySendMessage = (tempId) => {
        const newMessagesData = JSON.parse(JSON.stringify(messagesData));
        const findMessage = newMessagesData.find((m) => m.tempId === tempId);

        const obj = { ...findMessage, text: findMessage.encryptText };
        delete obj.encryptText;
        delete obj.error;

        socket.emit('sendOpenMessage', obj);
    };

    useEffect(() => {
        socket.off('receivingOpenMessage');
        socket.on('receivingOpenMessage', (data) => {
            if (data.sender === sender) return;

            getMessageRefetch();

            if (!open) {
                setMessagesCount(messagesCount + 1);
            }
        });

        socket.off('sendOpenMessageError');
        socket.on('sendOpenMessageError', (tempId) => {
            const newMessagesData = JSON.parse(JSON.stringify(messagesData));
            const findIndex = newMessagesData.findIndex((m) => m.tempId === tempId);

            if (findIndex !== -1) {
                newMessagesData[findIndex].error = true;
                setMessagesData(newMessagesData);
            }
        });

        socket.off('sendOpenMessageSuccess');
        socket.on('sendOpenMessageSuccess', ({ tempId, id }) => {
            const newMessagesData = JSON.parse(JSON.stringify(messagesData));
            const findIndex = newMessagesData.findIndex((m) => m.tempId === tempId);
            if (findIndex !== -1) {
                newMessagesData[findIndex].id = id;
                delete newMessagesData[findIndex].tempId;
                setMessagesData(newMessagesData);
            }
        });
    }, [messagesData, messagesCount]);

    const {
        data: getMessagesData,
        error: getMessagesError,
        refetch: getMessageRefetch,
    } = useQuery(GET_OPEN_MESSAGES, {
        variables: { id: sessionId },
        skip: _.isEmpty(user),
        pollInterval: 30000,
    });
    useEffect(() => {
        if (getMessagesError) {
            dispatch(alertError(`Cannot get Messages: ${getMessagesError.message}`));
        }

        if (
            getMessagesData &&
            getMessagesData.session &&
            !_.isEmpty(getMessagesData.session?.messages)
        ) {
            let decryptMessages = getMessagesData.session.messages.map((message) =>
                message.text
                    ? {
                          ...message,
                          text: decrypt(message.text, message.sender + message.session.id),
                      }
                    : message
            );

            setMessagesData(decryptMessages);
        }
    }, [getMessagesError, getMessagesData]);

    return (
        open && (
            <StyledCard style={containerStyle(mdUp)}>
                <StyledCardContent
                    style={{
                        display: 'grid',
                        gridTemplateRows: 'auto 1fr auto',
                    }}
                >
                    <div
                        ref={headerRef}
                        style={{
                            padding: '10px 20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            boxShadow: '1px 2px 12px 1px rgba(71, 77, 108, 0.04)',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {!mdUp && <GoBackButton onClick={onClose} />}
                            <Text $fontSize="20px" $bold>
                                IN CALL MESSAGE
                            </Text>
                        </div>

                        <div>
                            {mdUp && (
                                <IconButton onClick={onClose}>
                                    <CrossIcon fill="black" />
                                </IconButton>
                            )}
                        </div>
                    </div>
                    <div
                        style={{
                            height: contentHeight,
                            padding: '10px 0',
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <TextGroup
                            messagesData={messagesData}
                            handleRetrySendMessage={handleRetrySendMessage}
                            sender={sender}
                            showAvatar
                            handlePreview={() => {}}
                        />
                    </div>

                    <div ref={inputContainerRef}>
                        <ChatInput
                            pickerRef={pickerRef}
                            openPicker={openPicker}
                            onEmojiClick={onEmojiClick}
                            handleOpenPicker={handleOpenPicker}
                            inputValue={inputValue}
                            onChange={onChange}
                            handleAddLineBreak={handleAddLineBreak}
                            handleSendMessage={handleSendMessage}
                        />
                    </div>
                </StyledCardContent>
            </StyledCard>
        )
    );
};

MessagesCard.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    sender: PropTypes.string.isRequired,
    user: PropTypes.object.isRequired,
    sessionId: PropTypes.string.isRequired,
    messagesCount: PropTypes.number.isRequired,
    setMessagesCount: PropTypes.func.isRequired,
};
